import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { UpdateTeamRequest, UpdateTeamResponse } from '@pkg/types/api'

import api from '@/api'
import { isAdminRole, isFreeRole } from '@/helpers/team'
import { SENT_TEAM_INVITES_KEY } from '@/react-query/team-invites'
import { TEAM_MATES_KEY } from '@/react-query/team-members'

import { useUser } from './user'

export const TEAM_KEY = 'team'
export const TEAM_BILLING_KEY = 'teamBilling'
export const TEAM_CREATION_BILLING_PREVIEW_KEY = 'teamCreationBillingPreview'

export const useTeam = () => {
  const { data: user } = useUser()

  return useQuery({
    queryKey: [TEAM_KEY],
    queryFn: async () => {
      return (await api.getTeam()).data.team || null
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  })
}

export const useBillingInfo = () => {
  const team = useTeam()

  return useQuery({
    queryKey: [TEAM_BILLING_KEY],
    queryFn: async () => (await api.getTeamBillingInfo()).data.teamBillingInfo,
    enabled: !!team.data,
  })
}

export const useTeamCreationBillingPreview = (enabled: boolean) =>
  useQuery({
    queryKey: [TEAM_CREATION_BILLING_PREVIEW_KEY],
    queryFn: async () =>
      (await api.getTeamCreationBillingPreview()).data.personalPlan,
    enabled,
  })

export const useCreateTeam = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (team: { name: string }) => {
      const response = await api.createTeam(team)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([TEAM_KEY], () => ({
        name: data.team.name,
        id: data.team.id,
        userRole: data.team.userRole,
        isVisibilityEnabled: data.team.isVisibilityEnabled,
        enabledAdminVisibility: data.team.enabledAdminVisibility,
      }))
    },
  })
}

export const useUpdateTeam = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (team: UpdateTeamRequest) => {
      const response = await api.updateTeam(team)
      return response.data
    },
    onMutate: async (team) => {
      await queryClient.cancelQueries({ queryKey: [TEAM_KEY] })
      const previousTeam = queryClient.getQueryData([TEAM_KEY])
      queryClient.setQueryData(
        [TEAM_KEY],
        (oldTeam: UpdateTeamResponse['team']) => ({
          ...oldTeam,
          ...team,
        }),
      )
      return { previousTeam }
    },
    onError: (_err, _team, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData([TEAM_KEY], context.previousTeam)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY] })
    },
  })
}

export const useLeaveTeam = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.leaveTeam()
    },
    onSuccess: () => {
      queryClient.setQueryData([TEAM_KEY], () => null)
      queryClient.setQueryData([SENT_TEAM_INVITES_KEY], () => [])
      queryClient.setQueryData([TEAM_MATES_KEY], () => [])
    },
  })
}

export const useIsViewOnlyMember = () => {
  const { data: team, isLoading } = useTeam()

  if (isLoading || team?.userRole === undefined) {
    return false
  }

  return isFreeRole(team.userRole)
}

export const useIsAdmin = () => {
  const { data: team, isLoading } = useTeam()

  if (isLoading || team?.userRole === undefined) {
    return false
  }

  return isAdminRole(team.userRole)
}

export const useIsATSConnected = () => {
  const { data: team, isLoading } = useTeam()

  if (isLoading || team?.userRole === undefined) {
    return false
  }

  return team.isATSConnected
}
